/**
 * Global publish-readiness bus (web) — twin of mobile's
 * src/app/lib/publishReadiness.ts and a sibling of `lib/paywall.ts`,
 * but for the "finish setting up your profile before going public"
 * modal.
 *
 * Two emitters, same defence-in-depth shape as the paywall:
 *   1. `requestPublishReadiness()` — explicit, from the /home toggle
 *      handler when the user clicks "go public" but `publishReadiness`
 *      says they're not ready. The toggle stays put; the modal opens.
 *   2. `requestPublishReadiness()` from the convex-client interceptor
 *      in `convexDispatch.ts`, fired whenever a mutation throws the
 *      NOT_PUBLISH_READY ConvexError — the bypass-the-frontend safety
 *      net.
 *
 * A single `<PublishReadinessHost />` mounted at the app root
 * subscribes, reads the live `publishReadiness` query itself, and
 * renders the modal.
 *
 * No React deps so it can be imported from convexDispatch.ts (outside
 * the component tree).
 */

type Listener = () => void;

const listeners = new Set<Listener>();

export function requestPublishReadiness(): void {
  for (const fn of listeners) {
    try {
      fn();
    } catch {
      /* listener errors are non-fatal */
    }
  }
}

export function subscribeToPublishReadiness(fn: Listener): () => void {
  listeners.add(fn);
  return () => {
    listeners.delete(fn);
  };
}
